<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaProgressBar from '../base/GaiaProgressBar.vue';
import GaiaStatusIndicator, { type GaiaIndicatorStatus } from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';

const props = defineProps({
  /**
   * The design-system status, not a backend enum — callers translate through
   * `statusMaps.ts` (taskStatusMap) or their own payload's vocabulary, so this
   * row never interprets API values.
   */
  status: {
    type: String as PropType<GaiaIndicatorStatus>,
    required: true,
  },
  /** What the job is — a folder name, an operation. Ellipsised to one line. */
  label: {
    type: String,
    required: true,
  },
  /** Secondary line: "3 of 12 files", the current step, or the error text. */
  detail: {
    type: String,
    default: undefined,
  },
  /** Renders the progress bar while the work runs; omit when indeterminate. */
  percentage: {
    type: Number,
    default: undefined,
  },
});

/** A failed job's detail is the error — it reads in alert, not in grey. */
const detailTone = computed(() => (props.status === 'alert' ? 'alert' : 'tertiary'));
</script>

<template>
  <!-- One job in a GaiaTaskPanel: status dot, name, progress, and whatever
       actions the caller slots on the right. Both corner panels render this
       row, so a download and a demo-seed run are visually the same thing. -->
  <n-flex align="center" justify="space-between" :size="10" :wrap="false" class="gaia-task-row">
    <n-flex vertical :size="3" :style="{ flex: 1, minWidth: 0 }">
      <n-flex align="center" :size="7" :wrap="false" :style="{ minWidth: 0 }">
        <gaia-status-indicator :status="props.status" />
        <!-- Plain text inside n-ellipsis: nesting n-text here would get cloned
             into the hover tooltip and render dark-on-dark. -->
        <n-ellipsis :style="{ maxWidth: '230px' }">{{ props.label }}</n-ellipsis>
      </n-flex>

      <gaia-progress-bar
        v-if="props.percentage !== undefined"
        :percentage="props.percentage"
        :height="4"
      />

      <gaia-text v-if="props.detail" size="micro" :tone="detailTone">
        {{ props.detail }}
      </gaia-text>
    </n-flex>

    <n-flex v-if="$slots.actions" align="center" :size="4" :wrap="false" :style="{ flex: 'none' }">
      <slot name="actions" />
    </n-flex>
  </n-flex>
</template>
